import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Video, VideoOff, Mic, MicOff, PhoneOff, Users, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

const ConferenceGuestPage = () => {
  const { roomId } = useParams();
  const [roomInfo, setRoomInfo] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestId, setGuestId] = useState(null);
  const [isJoined, setIsJoined] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [error, setError] = useState(null);

  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const wsRef = useRef(null);

  useEffect(() => {
    if (roomId) {
      fetchRoomInfo();
    }
  }, [roomId]);

  const fetchRoomInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/calling/conference/${roomId}`);
      setRoomInfo(response.data);
      if (!response.data.is_customer_call) {
        setError('This conference does not allow guest access. Please ask for a new link.');
      }
    } catch (err) {
      setError('Conference not found or has ended.');
    }
  };

  const joinAsGuest = async () => {
    if (!guestName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/calling/conference/${roomId}/guest-join`,
        null,
        { params: { display_name: guestName } }
      );

      setGuestId(response.data.guest_id);
      setIsJoined(true);
      
      // Start local media
      await startLocalMedia();
      
      toast.success('Joined conference!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join conference');
    }
  };

  const startLocalMedia = async () => {
    try {
      const constraints = {
        audio: true,
        video: roomInfo?.call_type === 'video'
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error accessing media:', err);
      toast.error('Could not access camera/microphone. Please check permissions.');
    }
  };

  const leaveConference = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
    if (wsRef.current) {
      wsRef.current.close();
    }
    setIsJoined(false);
    toast.info('You left the conference');
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-red-500/20 mx-auto mb-6 flex items-center justify-center">
            <PhoneOff className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Conference Unavailable</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button
            onClick={() => window.close()}
            className="bg-gray-700 hover:bg-gray-600 text-white"
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (!roomInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // Pre-join screen
  if (!isJoined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-gray-900/90 border border-gray-700 rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 mx-auto mb-4 flex items-center justify-center">
              {roomInfo.call_type === 'video' ? (
                <Video className="w-10 h-10 text-cyan-400" />
              ) : (
                <Mic className="w-10 h-10 text-cyan-400" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{roomInfo.name}</h1>
            <p className="text-gray-400">
              {roomInfo.call_type === 'video' ? 'Video Conference' : 'Audio Conference'}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              <Users className="w-4 h-4 inline mr-1" />
              {roomInfo.participants} participant{roomInfo.participants !== 1 ? 's' : ''} waiting
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Your Name</label>
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Enter your name..."
                className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                onKeyPress={(e) => e.key === 'Enter' && joinAsGuest()}
              />
            </div>

            <Button
              onClick={joinAsGuest}
              disabled={!guestName.trim()}
              className="w-full py-6 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white text-lg font-semibold rounded-xl disabled:opacity-50"
            >
              Join Conference
            </Button>

            <p className="text-xs text-gray-500 text-center">
              By joining, you agree to our terms of service
            </p>
          </div>
        </div>
      </div>
    );
  }

  // In-call view
  return (
    <div className="fixed inset-0 bg-gray-950">
      {/* Video area */}
      <div className="h-[calc(100%-80px)] p-4 flex items-center justify-center">
        {roomInfo.call_type === 'video' ? (
          <div className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden bg-gray-900">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            {isVideoOff && (
              <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                  <span className="text-4xl font-bold text-white">
                    {guestName.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
            )}
            <div className="absolute bottom-4 left-4 px-4 py-2 bg-black/60 backdrop-blur-sm rounded-lg text-white">
              {guestName} (You)
              {isMuted && <span className="text-red-400 ml-2">(muted)</span>}
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 mx-auto mb-6 flex items-center justify-center">
              <span className="text-5xl font-bold text-white">
                {guestName.charAt(0).toUpperCase()}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{guestName}</h2>
            <p className="text-gray-400">In conference: {roomInfo.name}</p>
            {/* Audio wave animation */}
            <div className="flex items-end justify-center gap-1 h-8 mt-6">
              {[1,2,3,4,5].map(i => (
                <div
                  key={i}
                  className={`w-1 rounded-full animate-pulse ${isMuted ? 'bg-gray-600' : 'bg-cyan-500'}`}
                  style={{
                    height: isMuted ? '8px' : `${Math.random() * 24 + 8}px`,
                    animationDelay: `${i * 0.1}s`
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Controls bar */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-black/80 backdrop-blur-sm border-t border-gray-800">
        <div className="h-full flex items-center justify-center gap-4">
          <Button
            onClick={toggleMute}
            className={`w-14 h-14 rounded-full ${isMuted ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-700 hover:bg-gray-600'}`}
          >
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </Button>
          
          {roomInfo.call_type === 'video' && (
            <Button
              onClick={toggleVideo}
              className={`w-14 h-14 rounded-full ${isVideoOff ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            >
              {isVideoOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
            </Button>
          )}

          <Button
            onClick={leaveConference}
            className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600"
          >
            <PhoneOff className="w-6 h-6" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConferenceGuestPage;
